import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useDialogChrome } from './dialog.js';
import { t } from '../i18n/index.js';

/**
 * In-app modal: centred card on a backdrop, focus trapped inside, Esc closes, Enter confirms
 * (when not typing in a multi-line field). Used directly for custom content and through
 * useConfirm() as a drop-in for window.confirm.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  size = 'sm',
  labelledBy,
}: {
  open: boolean;
  title?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  labelledBy?: string;
}) {
  const card = useRef<HTMLDivElement>(null);

  useDialogChrome({ open, onClose, root: card });

  if (!open) return null;
  const titleId = labelledBy ?? 'modal-title';
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={card}
        className={`modal modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {title && (
          <h3 id={titleId} className="modal-title">
            {title}
          </h3>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export type ConfirmOptions = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red primary button for destructive actions. */
  danger?: boolean;
  /** Require typing this word before the confirm button enables (e.g. "RUN"). */
  typeToConfirm?: string;
};

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

const ConfirmContext = createContext<((o: ConfirmOptions) => Promise<boolean>) | null>(null);

/** Mount once near the app root; then `const confirm = useConfirm(); if (await confirm({...}))`. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [typed, setTyped] = useState('');
  const ask = useCallback(
    (o: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setTyped('');
        setPending({ ...o, resolve });
      }),
    [],
  );
  const finish = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };
  const canConfirm = !pending?.typeToConfirm || typed === pending.typeToConfirm;
  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <Modal
        open={!!pending}
        title={pending?.title ?? ''}
        onClose={() => finish(false)}
        footer={
          pending && (
            <>
              <button type="button" className="secondary" onClick={() => finish(false)}>
                {pending.cancelLabel ?? t('cancel')}
              </button>
              <button
                type="button"
                className={pending.danger ? 'danger' : ''}
                onClick={() => finish(true)}
                disabled={!canConfirm}
                data-autofocus={pending.typeToConfirm ? undefined : ''}
              >
                {pending.confirmLabel ?? t('confirm')}
              </button>
            </>
          )
        }
      >
        {pending?.body && <div className="modal-text">{pending.body}</div>}
        {pending?.typeToConfirm && (
          <form
            className="modal-type"
            onSubmit={(e) => {
              e.preventDefault();
              if (canConfirm) finish(true);
            }}
          >
            <label>
              {t('type_to_confirm', { word: pending.typeToConfirm })}
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={pending.typeToConfirm}
                aria-label={t('type_to_confirm', { word: pending.typeToConfirm })}
                autoComplete="off"
                data-autofocus=""
              />
            </label>
          </form>
        )}
        {!pending?.typeToConfirm && (
          // Enter confirms when the primary button is focused (default); this catches Enter
          // pressed elsewhere in the dialog.
          <EnterToConfirm onConfirm={() => finish(true)} />
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

function EnterToConfirm({ onConfirm }: { onConfirm: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A') return;
      e.preventDefault();
      onConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onConfirm]);
  return null;
}

export function useConfirm(): (o: ConfirmOptions) => Promise<boolean> {
  const ask = useContext(ConfirmContext);
  if (!ask) throw new Error('useConfirm needs a ConfirmProvider');
  return ask;
}
